import { useContext, useState } from 'react';
import styled from '@emotion/styled';
import { createId } from '@paralleldrive/cuid2';
import type { Chat, HcpSearchResult } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { useSelectedQueryParentIdentifier } from '@trinity/chat-components/components/Chat/hooks/useSelectedQueryParentIdentifier';
import { useSubmitQueryMutation } from '@trinity/chat-components/components/Chat/hooks/useSubmitQueryMutation';
import { SelectHcp } from '@trinity/chat-components/components/Chat/KolSearch.Form';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { SearchQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';

type Props = {
  item: Chat.Attachment.HCPSearch;
  className?: string;
};

const Label = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  marginBottom: 10,
}));

const FormRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  margin-top: 10px;
`;

const SearchButtonSection = styled.div`
  margin-top: 10px;
`;

const createHcpQuery = (
  messageTemplate: string | undefined,
  hcpName: string,
  defaultPrefix: string
): string => {
  return messageTemplate
    ? messageTemplate.replace('[name]', hcpName.trim())
    : `${defaultPrefix} ${hcpName.trim()}`;
};

export const HCPSearchAttachment = (props: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const chat = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const dispatch = useContext(ChatStateDispatchContext);
  const submitQueryMutation = useSubmitQueryMutation();
  const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();

  const handleHcpSelection = (hcp: HcpSearchResult) => {
    setIsSubmitted(true);
    const subject = {
      id: hcp.hcpIdentifier,
      name: hcp.name,
    };
    const initialUserMessage = createHcpQuery(
      props.item.data.hcpSelectedAction.initialMessageTemplate,
      subject.name,
      'Deep dive into'
    );

    if (chat.context.type !== ChatContextType.Agent) {
      throw new Error('Cannot open new chat for subject when chat context is not Agent');
    }

    dispatch({
      type: 'new-chat-started',
      payload: {
        brandIdentifier,
        context: {
          ...chat.context,
          data: {
            ...chat.context.data,
            subject,
          },
        },
        pendingQuery: initialUserMessage,
      },
    });
  };

  const handleHcpSearch = () => {
    setIsSubmitted(true);
    const query = createHcpQuery(props.item.data.searchAction.messageTemplate, inputValue, 'Search for HCP:');

    const queryIdentifier = createId();
    const parentQueryIdentifier = getSelectedQueryParentIdentifier();

    submitQueryMutation.mutate({
      identifier: queryIdentifier,
      parentQueryIdentifier: parentQueryIdentifier ?? null,
      query,
      action: null,
    });
  };

  const apiClient = useChatApiClient();

  return (
    <>
      <FormRow>
        <Label>Name</Label>
        <SelectHcp<HcpSearchResult>
          placeholder="Type HCP Name"
          disabled={isSubmitted}
          onInputFocus={() => {}}
          onInputBlur={() => {}}
          onSelection={handleHcpSelection}
          onInputChange={setInputValue}
          inputValue={inputValue}
          queryFn={apiClient.search.searchHcpsByName}
        />
      </FormRow>
      <SearchButtonSection>
        <SearchQueryHint
          disabled={inputValue.trim().length === 0 || isSubmitted}
          onClick={handleHcpSearch}
          value="Search for HCP"
        />
      </SearchButtonSection>
    </>
  );
};
